/**
 * Minimal DOM helpers.
 *
 * No framework. The dashboard is a few dozen labelled numbers that update at
 * 10 Hz; a reactive runtime would be more code to read than the panels
 * themselves, and this project is meant to be readable end to end.
 */

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function panel(title: string, className = ''): { root: HTMLElement; body: HTMLElement } {
  const root = el('section', `panel ${className}`.trim());
  root.appendChild(el('h2', 'panel-title', title));
  const body = el('div', 'panel-body');
  root.appendChild(body);
  return { root, body };
}

/** A label/value row whose value can be updated in place. */
export class Field {
  readonly row: HTMLElement;
  private valueEl: HTMLElement;

  constructor(label: string, initial = '—', hint?: string) {
    this.row = el('div', 'field');
    const labelEl = el('span', 'field-label', label);
    if (hint) labelEl.title = hint;
    this.valueEl = el('span', 'field-value', initial);
    this.row.append(labelEl, this.valueEl);
  }

  set(value: string, tone?: 'ok' | 'warn' | 'bad' | 'muted'): void {
    if (this.valueEl.textContent !== value) this.valueEl.textContent = value;
    const cls = `field-value${tone ? ` tone-${tone}` : ''}`;
    if (this.valueEl.className !== cls) this.valueEl.className = cls;
  }
}

export class FieldGroup {
  readonly root: HTMLElement;
  private fields = new Map<string, Field>();

  constructor(className = 'fields') {
    this.root = el('div', className);
  }

  add(key: string, label: string, hint?: string): Field {
    const f = new Field(label, '—', hint);
    this.fields.set(key, f);
    this.root.appendChild(f.row);
    return f;
  }

  set(key: string, value: string, tone?: 'ok' | 'warn' | 'bad' | 'muted'): void {
    this.fields.get(key)?.set(value, tone);
  }
}

export function button(
  label: string,
  onClick: () => void,
  className = '',
): HTMLButtonElement {
  const b = el('button', `btn ${className}`.trim(), label);
  b.type = 'button';
  b.addEventListener('click', onClick);
  return b;
}

export function select<T extends string>(
  options: { value: T; label: string }[],
  value: T,
  onChange: (v: T) => void,
): HTMLSelectElement {
  const s = el('select', 'select');
  for (const o of options) {
    const opt = el('option');
    opt.value = o.value;
    opt.textContent = o.label;
    s.appendChild(opt);
  }
  s.value = value;
  s.addEventListener('change', () => onChange(s.value as T));
  return s;
}

export function checkbox(
  label: string,
  checked: boolean,
  onChange: (v: boolean) => void,
): HTMLLabelElement {
  const wrap = el('label', 'checkbox');
  const input = el('input');
  input.type = 'checkbox';
  input.checked = checked;
  input.addEventListener('change', () => onChange(input.checked));
  wrap.append(input, el('span', '', label));
  return wrap;
}

export interface NumberFieldOptions {
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  hint?: string;
}

/** A labelled numeric input. Returns the row plus a setter for external updates. */
export function numberField(
  label: string,
  value: number,
  onChange: (v: number) => void,
  options: NumberFieldOptions = {},
): { row: HTMLElement; set: (v: number) => void } {
  const row = el('label', 'number-field');
  const labelEl = el('span', 'number-label', options.unit ? `${label} (${options.unit})` : label);
  if (options.hint) labelEl.title = options.hint;
  const input = el('input');
  input.type = 'number';
  input.value = String(value);
  if (options.min !== undefined) input.min = String(options.min);
  if (options.max !== undefined) input.max = String(options.max);
  input.step = String(options.step ?? 1);
  input.addEventListener('change', () => {
    const v = Number(input.value);
    if (Number.isFinite(v)) onChange(v);
  });
  row.append(labelEl, input);
  return { row, set: (v) => { input.value = String(v); } };
}

/** A labelled slider with a live readout. */
export function slider(
  label: string,
  value: number,
  min: number,
  max: number,
  step: number,
  onChange: (v: number) => void,
  format: (v: number) => string = (v) => String(v),
): { row: HTMLElement; set: (v: number) => void } {
  const row = el('div', 'slider-row');
  const head = el('div', 'slider-head');
  const labelEl = el('span', 'slider-label', label);
  const readout = el('span', 'slider-value', format(value));
  head.append(labelEl, readout);
  const input = el('input');
  input.type = 'range';
  input.min = String(min);
  input.max = String(max);
  input.step = String(step);
  input.value = String(value);
  input.addEventListener('input', () => {
    const v = Number(input.value);
    readout.textContent = format(v);
    onChange(v);
  });
  row.append(head, input);
  return {
    row,
    set: (v) => {
      input.value = String(v);
      readout.textContent = format(v);
    },
  };
}

export function fmtMs(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return `${v.toFixed(v < 10 ? 1 : 0)} ms`;
}

export function fmtPct(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

/** Trigger a browser download of a text file. */
export function downloadText(filename: string, text: string, mime = 'text/plain'): void {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = el('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
